if (!customElements.get('ozana-reduction-stack')) {
  customElements.define(
    'ozana-reduction-stack',
    class extends HTMLElement {
      connectedCallback() {
        this.cards = [...this.querySelectorAll('[data-reduction-card]')];
        if (!this.cards.length) return;
        this.index = 0;
        this.delay = Math.max(700, Number(this.dataset.delay || 1400));
        this.render();
        if (!matchMedia('(prefers-reduced-motion: reduce)').matches && this.cards.length > 1) {
          this.timer = setInterval(() => {
            this.index = (this.index + 1) % this.cards.length;
            this.render();
          }, this.delay);
        }
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      render() {
        const visible = Math.min(3, this.cards.length);
        this.cards.forEach((card, cardIndex) => {
          const rank = (cardIndex - this.index + this.cards.length) % this.cards.length;
          const active = rank < visible;
          card.classList.toggle('is-active', active);
          card.dataset.stackRank = active ? String(rank) : '';
          card.style.setProperty('--oz-stack-y', `${rank * -31}px`);
          card.style.setProperty('--oz-stack-scale', `${1 - rank * 0.035}`);
          card.style.setProperty('--oz-stack-z', `${visible - rank}`);
          card.style.setProperty('--oz-stack-brightness', `${1 - rank * 0.1}`);
          card.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      }
    }
  );
}

if (!customElements.get('ozana-media-slider')) {
  customElements.define(
    'ozana-media-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-media-track]');
        this.slides = [...this.querySelectorAll('[data-media-slide]')];
        this.dots = [...this.querySelectorAll('[data-media-dot]')];
        this.previousButton = this.querySelector('[data-media-previous]');
        this.nextButton = this.querySelector('[data-media-next]');
        if (!this.track || this.slides.length < 2) return;

        this.activeIndex = 0;
        this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
        this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => {
            const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
            this.update(Math.min(this.slides.length - 1, Math.max(0, index)));
          });
        }, { passive: true });
        this.update(0);
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.scrollFrame);
      }

      goTo(index) {
        const nextIndex = (index + this.slides.length) % this.slides.length;
        this.track.scrollTo({ left: nextIndex * this.track.clientWidth, behavior: 'smooth' });
        this.update(nextIndex);
      }

      update(index) {
        this.activeIndex = index;
        this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      }
    }
  );
}

const PRODUCT_PAGE_SELECTOR = '.ozana-product-detail';
const HOVER_ACCORDION_SELECTOR = [
  '.ozana-product-detail__cartridge-steps',
  '.ozana-product-detail__install-steps'
].join(',');

function animateProductDetail(detail, shouldOpen) {
  detail.open = shouldOpen;
}

function openAccordionDetail(detail, closeSiblings = true) {
  if (closeSiblings) {
    [...detail.parentElement.children]
      .filter((item) => item.matches?.('details') && item !== detail && item.open)
      .forEach((item) => animateProductDetail(item, false));
  }
  animateProductDetail(detail, true);
}

function prepareProductAccordions(productPage) {
  const hoverQuery = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

  productPage.querySelectorAll(HOVER_ACCORDION_SELECTOR).forEach((group) => {
    if (group.dataset.ozanaAccordionReady === 'true') return;
    group.dataset.ozanaAccordionReady = 'true';

    [...group.children].filter((item) => item.matches?.('details')).forEach((detail) => {
      const summary = detail.querySelector(':scope > summary');
      if (!summary) return;

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (detail.open) animateProductDetail(detail, false);
        else openAccordionDetail(detail);
      });

      detail.addEventListener('pointerenter', (event) => {
        if (!hoverQuery.matches || event.pointerType === 'touch') return;
        openAccordionDetail(detail);
      });

      summary.addEventListener('focus', () => {
        if (hoverQuery.matches) openAccordionDetail(detail);
      });
    });
  });
}

function prepareProductSpecificationAccordions(productPage) {
  const specificationCards = [...productPage.querySelectorAll('.ozana-product-detail__spec-card')];
  if (!specificationCards.length || productPage.dataset.ozanaSpecsReady === 'true') return;
  productPage.dataset.ozanaSpecsReady = 'true';

  const setInitialState = () => {
    specificationCards.forEach((card) => card.removeAttribute('open'));
  };

  specificationCards.forEach((card) => {
    card.addEventListener('toggle', () => {
      if (!card.open) return;
      specificationCards.forEach((otherCard) => {
        if (otherCard !== card) otherCard.removeAttribute('open');
      });
    });
  });

  setInitialState();
}

function prepareProductPurchaseOptions(productPage) {
  productPage.querySelectorAll('[data-ozana-product-form]').forEach((form) => {
    if (form.dataset.ozanaPurchaseReady === 'true') return;

    const choices = [...form.querySelectorAll('[data-ozana-selling-plan-choice]')];
    const sellingPlanInput = form.querySelector('[data-ozana-selling-plan-input]');
    const price = productPage.querySelector('[data-ozana-product-price]');
    if (!choices.length || !sellingPlanInput) return;

    form.dataset.ozanaPurchaseReady = 'true';
    const updateChoice = (choice) => {
      const sellingPlanId = choice.value;
      sellingPlanInput.value = sellingPlanId;
      sellingPlanInput.disabled = sellingPlanId === '';
      if (price && choice.dataset.price) price.textContent = choice.dataset.price;
    };

    choices.forEach((choice) => {
      choice.addEventListener('change', () => {
        if (choice.checked) updateChoice(choice);
      });
    });

    const selectedChoice = choices.find((choice) => choice.checked) || choices[0];
    updateChoice(selectedChoice);
  });
}

function prepareProductBenefitTabs(productPage) {
  const tabs = [...productPage.querySelectorAll('[data-ozana-benefit-tab]')];
  const panels = [...productPage.querySelectorAll('[data-ozana-benefit-panel]')];
  if (!tabs.length || !panels.length || productPage.dataset.ozanaBenefitsReady === 'true') return;

  productPage.dataset.ozanaBenefitsReady = 'true';
  const activate = (activeTab, shouldFocus = false) => {
    tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      if (shouldFocus && selected) tab.focus();
    });

    panels.forEach((panel) => {
      const selected = panel.id === activeTab.getAttribute('aria-controls');
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
    });
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activate(tab));
    tab.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let nextIndex = index;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      activate(tabs[nextIndex], true);
    });
  });

  activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
}

function prepareProductPages(root = document) {
  root.querySelectorAll(PRODUCT_PAGE_SELECTOR).forEach((productPage) => {
    prepareProductAccordions(productPage);
    prepareProductSpecificationAccordions(productPage);
    prepareProductPurchaseOptions(productPage);
    prepareProductBenefitTabs(productPage);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => prepareProductPages(), { once: true });
} else {
  prepareProductPages();
}

document.addEventListener('shopify:section:load', (event) => prepareProductPages(event.target));
